/**
 * Slides do banner rotativo da home.
 *
 * Os slides das AÇÕES (Flash FEO, Plantão FEO) ficam "guardados" aqui e só
 * entram no ar quando a ação está ativa em /admin/campanhas. O slide de
 * "Lote Promo" das pós sai do ar enquanto o Plantão FEO está ativo.
 *
 * Ordem padrão (nenhuma ação ativa): Lote Promo → CBFEO → Certificações → Institucional.
 */
import type React from "react";

import { ACOES, EMPTY_ACOES_STATE, type AcoesState } from "@/lib/campanhas/acoes";
import { BLACKFEO_TAGLINE, BLACKFEO_TAGLINE_CLS } from "@/lib/black-november/tagline";

import hero from "@/assets/feo/hero.jpg";
import hero800 from "@/assets/feo/hero-800.webp";
import hero1280 from "@/assets/feo/hero-1280.webp";
import hero1920 from "@/assets/feo/hero-1920.webp";
import cbfeoWallpaper from "@/assets/cbfeo/cbfeo-wallpaper-connection.webp.asset.json";
import cbfeoLogo2026 from "@/assets/cbfeo/cbfeo-logo-2026.png.asset.json";
import cbfeoBanner from "@/assets/cbfeo/cbfeo-banner-home.webp.asset.json";
import bannerPos6 from "@/assets/feo/banner-pos-6areas.webp";
import lotepromoBg from "@/assets/loteprom-bg.webp.asset.json";
import flashfeoLogo from "@/assets/flashfeo/logo.png.asset.json";
import flashfeoPattern from "@/assets/flashfeo-pattern.webp.asset.json";
import certSelo from "@/assets/certificacao/selo-variacao.png.asset.json";
import certHexBg from "@/assets/certificacao/hex-bg.jpg.asset.json";
import { CERT_DATA } from "@/lib/certificacao/data";
import { CERT_META } from "@/lib/certificacao/config";
import semanaLogoHorizontal from "@/assets/semanadafisio/logo-horizontal.png.asset.json";
import semanaBannerBg from "@/assets/semanadafisio/banner-bg.png.asset.json";

import plantaofeoLogo from "@/assets/plantaofeo/plantaofeo-logo.png.asset.json";
import logoBlackOficial from "@/assets/black2x1/black-logo-oficial.webp.asset.json";
import logoBlackfeo from "@/assets/blackfeo/logo-1.webp.asset.json";
import aprovafeoBanner from "@/assets/aprovafeo/aprovafeo-orto-banner-nova.webp.asset.json";

export const HERO_IMG = hero1280;
export const HERO_SRCSET = `${hero800} 800w, ${hero1280} 1280w, ${hero1920} 1920w`;
export const HERO_SIZES = "100vw";

export type Slide = {
  key: string;
  image: string;
  imageAlt: string;
  logo?: string;
  logoAlt?: string;
  logoClass?: string;
  noImage?: boolean;
  bgImage?: string;
  bgImageSrcSet?: string;
  bgImageSizes?: string;
  bgImageClass?: string;
  bgOverlay?: string;
  bgStyle?: string;
  stripes?: boolean;
  align?: "center" | "left";
  eyebrow?: string;
  title: React.ReactNode;
  titleClass?: string;
  desc: string;
  ctaLabel: string;
  ctaHref: string;
  ctaInternal: boolean;
  bgClass: string;
  accent: string;
  ctaClass: string;
  /** Gradiente do blur decorativo no topo (ex: arco-íris). */
  topBlur?: string;
  /** Gradiente da linha divisória abaixo do desc. */
  divider?: string;
  /** Linha de destaque laranja abaixo do desc. */
  tagline?: string;
  /** Cor da tagline, quando o slide não usa o laranja padrão. */
  taglineClass?: string;
  descClass?: string;
  /** Pills de destaque exibidas abaixo da linha divisória. */
  pills?: string[];
  /** Linhas curtas em destaque abaixo do título (uma por linha). */
  descLines?: string[];

};

/** Slide da ação Flash FEO — guardado; entra no ar só com a ação ativa. */
const SLIDE_FLASHFEO: Slide = {
  key: "flashfeo",
  image: "",
  imageAlt: "Flash FEO, ofertas relâmpago por apenas 24 horas",
  noImage: true,
  bgStyle: "linear-gradient(180deg, #14237a 0%, #070e3a 100%)",
  bgImage: flashfeoPattern.url,
  bgImageClass: "object-cover object-center opacity-100",
  logo: flashfeoLogo.url,
  logoAlt: "Flash FEO",
  logoClass: "h-20 sm:h-28 md:h-36 lg:h-44 mb-5 sm:mb-7",
  title: (
    <span className="inline-block max-w-[34ch]">
      Ofertas relâmpago
      <br />
      <span className="text-[#FFD400]">por apenas 24 horas</span>
    </span>
  ),
  desc: "Certificações, Star Cursos e Agente IA com preços imperdíveis.",
  ctaLabel: "APROVEITAR OFERTAS",
  ctaHref: "/flashfeo",
  ctaInternal: true,
  bgClass: "bg-[#1B39C9]",
  accent: "#FFD400",
  ctaClass: "bg-[#FFD400] hover:brightness-110 text-[#070e3a]",
};

/** Slide da ação Plantão FEO — guardado; entra no ar só com a ação ativa. */
const SLIDE_PLANTAOFEO: Slide = {
  key: "plantaofeo",
  image: "",
  imageAlt: "Plantão FEO, pós-graduações com até 60% de desconto",
  noImage: true,
  bgStyle: "linear-gradient(180deg, #141d5c 0%, #05091f 100%)",
  bgImage: lotepromoBg.url,
  bgImageClass: "object-cover object-center opacity-45",
  logo: plantaofeoLogo.url,
  logoAlt: "Plantão FEO",
  logoClass: "h-24 sm:h-32 md:h-40 lg:h-44 mb-5 sm:mb-7",
  title: (
    <span className="inline-block max-w-[34ch]">
      Pós-graduações com
      <br />
      <span className="text-[#F5C518]">até 60% de desconto</span>
    </span>
  ),
  desc: "As 6 pós-graduações da FEO com valor imperdível!",
  ctaLabel: "APROVEITAR OFERTAS",
  ctaHref: "/plantaofeo",
  ctaInternal: true,
  bgClass: "bg-[#05091f]",
  accent: "#F5C518",
  ctaClass: "bg-[#F5C518] hover:brightness-110 text-[#0A0F2C]",
};

/** Slide da ação Semana da Fisioterapia — guardado; entra no ar só com a ação ativa. */
const SLIDE_SEMANADAFISIO: Slide = {
  key: "semanadafisio",
  image: "",
  imageAlt: "Semana da Fisioterapia, até 80% de desconto",
  noImage: true,
  bgStyle: "linear-gradient(180deg, #0B565C 0%, #04353A 100%)",
  bgImage: semanaBannerBg.url,
  bgImageClass: "object-cover object-center opacity-100",
  bgOverlay: "linear-gradient(180deg, rgba(4,45,50,0.35) 0%, rgba(4,45,50,0.5) 100%)",
  logo: semanaLogoHorizontal.url,
  logoAlt: "Semana da Fisioterapia",
  logoClass: "h-14 sm:h-20 md:h-24 lg:h-28 mb-5 sm:mb-7 mx-auto",
  title: <span className="inline-block max-w-[34ch] text-[#4CD964]">Até 80% de desconto</span>,
  desc: "Pós-graduações, Certificações, Star Cursos e Agente IA com valores exclusivos. Aproveite a sua semana da melhor maneira.",
  tagline: "ÚLTIMAS HORAS",

  ctaLabel: "APROVEITAR OFERTAS",
  ctaHref: "/semanadafisio",
  ctaInternal: true,
  bgClass: "bg-[#04353A]",
  accent: "#4CD964",
  ctaClass: "bg-[#4CD964] hover:brightness-110 text-[#04353A]",

};

const SLIDE_CBFEO: Slide = {
  key: "cbfeo",
  image: cbfeoBanner.url,
  imageAlt: "II CBFEO 2026, Congresso Brasileiro de Fisioterapia em Ortopedia e Traumatologia",
  noImage: true,
  bgImage: cbfeoWallpaper.url,
  bgOverlay:
    "linear-gradient(180deg, rgba(3,10,32,0.55) 0%, rgba(3,10,32,0.7) 60%, rgba(3,10,32,0.85) 100%)",
  logo: cbfeoLogo2026.url,
  logoAlt: "II CBFEO 2026",
  eyebrow: "TRANSMISSÃO ONLINE · 18 E 19 DE SETEMBRO",
  title: <>II CBFEO&nbsp;ao vivo, de onde você estiver</>,
  desc: "Dois dias de imersão com os maiores nomes da fisioterapia musculoesquelética do Brasil. Assista ao vivo com certificado por R$ 97.",
  ctaLabel: "Garantir minha vaga",
  ctaHref: "/cbfeo-ao-vivo",
  ctaInternal: true,
  bgClass: "bg-[#030a20]",
  accent: "#22d3ee",
  ctaClass: "bg-[#22d3ee] hover:brightness-110 text-[#05122a]",
};

/** Lote promocional das pós — sai do ar enquanto o Plantão FEO está ativo. */
const SLIDE_POS: Slide = {
  key: "pos",
  image: bannerPos6,
  imageAlt: "As 6 pós-graduações da Fisio em Ortopedia",
  noImage: true,
  bgImage: lotepromoBg.url,
  eyebrow: "LOTE PROMOCIONAL",
  title: (
    <span className="inline-block max-w-[34ch]">
      As 6 pós-graduações da FEO
      <br />
      com <span className="text-[#f5c451]">condição especial</span>
    </span>
  ),
  desc: "Orto, Dor, Esporte, Gerontologia, Traumato e Prescrição de Exercícios. 360h, 100% online e reconhecidas pelo MEC. Aproveite o lote promocional.",
  ctaLabel: "Ver pós-graduações",
  ctaHref: "/pos",
  ctaInternal: true,
  bgClass: "bg-[#05122a]",
  accent: "#f5c451",
  ctaClass: "bg-[#f5c451] hover:brightness-110 text-[#13123a]",
};

/** Slide do catálogo de certificações — espelho do hero de /certificacao. */
const CERT_SELO_VARIACAO = certSelo.url;

const SLIDE_CERT: Slide = {
  key: "cert",
  image: "",
  imageAlt: "As 6 certificações da Fisio em Ortopedia",
  noImage: true,
  bgImage: certHexBg.url,
  bgImageClass: "object-cover object-center opacity-80",
  bgOverlay: [
    "radial-gradient(70% 60% at 50% 0%, rgba(31,95,209,0.45), transparent 72%)",
    "radial-gradient(115% 95% at 50% 32%, rgba(2,3,15,0) 34%, rgba(2,3,15,0.85) 100%)",
    "linear-gradient(180deg, rgba(10,18,64,0.5) 0%, rgba(5,6,28,0.72) 55%, rgba(2,3,15,0.92) 100%)",
  ].join(", "),
  logo: CERT_SELO_VARIACAO,
  logoAlt: "Certificações Fisio em Ortopedia",
  logoClass: "h-20 sm:h-24 md:h-28 mb-4 sm:mb-5",
  title: (
    <span className="inline-block max-w-[34ch]">
      Aprimore suas <span className="font-semibold">Técnicas de Tratamento</span>
    </span>
  ),
  desc: "Cursos 100% online com o que há de mais novo na ciência. Certificações repletas de aplicações clínicas e aulas práticas.",
  ctaLabel: "Ver certificações",
  ctaHref: "/certificacao",
  ctaInternal: true,
  bgClass: "bg-[#05061c]",
  accent: "#4FD9FF",
  ctaClass:
    "bg-white/10 hover:bg-white/15 text-white border border-white/20 backdrop-blur-sm",
};

/**
 * Slide da Certificação em Quadril (lançamento pelo Lovable).
 * Fica desativado por padrão em /admin/banner: ativar no dia do lançamento.
 */
const SLIDE_CERT_QUADRIL: Slide = {
  key: "certquadril",
  image: "",
  imageAlt: "Certificação em Quadril, lançamento da Fisio em Ortopedia",
  noImage: true,
  bgImage: CERT_DATA.quadril.heroBg ?? undefined,
  bgImageClass: "object-cover object-center opacity-90",
  bgOverlay: [
    "radial-gradient(70% 60% at 50% 0%, rgba(255,51,102,0.35), transparent 72%)",
    "linear-gradient(180deg, rgba(9,8,32,0.45) 0%, rgba(9,8,32,0.72) 55%, rgba(9,8,32,0.92) 100%)",
  ].join(", "),
  logo: CERT_DATA.quadril.logo ?? undefined,
  logoAlt: "Certificação em Quadril",
  logoClass: "h-14 sm:h-[4.2rem] md:h-[4.9rem] mb-4 sm:mb-5 mx-auto",
  eyebrow: "LANÇAMENTO · OFERTA EXCLUSIVA NO GRUPO",
  title: (
    <span className="inline-block max-w-[34ch]">
      Domine a avaliação e a
      <br />
      reabilitação do <span className="text-white">quadril</span>
    </span>
  ),
  desc: "Curso 100% online, 60h, com casos clínicos reais e aulas práticas",
  ctaLabel: "ENTRAR NO GRUPO DE PRÉ-MATRÍCULA",
  ctaHref: CERT_META.quadril.path,
  ctaInternal: true,
  bgClass: "bg-[#090820]",
  accent: "#FF3366",
  ctaClass: "bg-[#FF3366] hover:brightness-110 text-white",
};

const SLIDE_INSTITUCIONAL: Slide = {
  key: "institucional",
  image: hero,
  imageAlt: "Fisioterapeutas em atendimento clínico de ortopedia",
  noImage: true,
  bgImage: hero1280,
  bgImageSrcSet: HERO_SRCSET,
  bgImageSizes: HERO_SIZES,
  bgImageClass: "object-cover object-[65%_15%] md:object-[70%_20%] opacity-60",
  bgOverlay:
    "linear-gradient(to right, #13123a 0%, rgba(19,18,58,0.85) 45%, rgba(19,18,58,0.1) 100%)",
  align: "left",
  eyebrow: "Fisio em Ortopedia",
  title: (
    <>
      Feito por e para
      <br />
      <span className="text-[#95a2ff]">fisioterapeutas</span>
    </>
  ),
  desc: "Desde 2019, o Portal Fisio em Ortopedia vem democratizando o acesso à educação de qualidade na área da fisioterapia musculoesquelética.",
  ctaLabel: "Conheça as pós-graduações",
  ctaHref: "/pos",
  ctaInternal: true,
  bgClass: "bg-[#13123a]",
  accent: "#95a2ff",
  ctaClass: "bg-[#95a2ff] hover:brightness-110 text-[#13123a]",
};

/** Slide institucional da BLACK 2x1 2026 (Pós FEO em Dobro). */
const SLIDE_BLACK2X1: Slide = {
  key: "black2x1",
  image: "",
  imageAlt: "",
  noImage: true,
  logo: logoBlackOficial.url,
  logoAlt: "Pós FEO em Dobro",
  logoClass: "h-20 sm:h-28 md:h-32 mb-4 sm:mb-6",
  title: <span className="text-[#c39cf5]">A maior oferta da nossa história</span>,
  desc: "",
  descLines: ["2 pós-graduações.", "Pague apenas 1.", "Acesso vitalício à Star Cursos."],
  ctaLabel: "Saiba mais",
  ctaHref: "/black-lista?ponto=slide-home",
  ctaInternal: false,
  bgClass: "bg-[#07060f] text-white",
  bgStyle:
    "radial-gradient(40rem 28rem at 100% 0%, rgba(124,58,237,0.28), transparent 70%), radial-gradient(36rem 24rem at 0% 100%, rgba(124,58,237,0.2), transparent 70%)",
  accent: "#b794f6",
  ctaClass: "bg-[#2fe0d6] text-[#07060f] uppercase font-extrabold hover:brightness-110",
};

/** Slide BLACKFEO (substitui o da Black em Dobro de 04/11 a 30/11 ou na prévia). */
export const SLIDE_BLACKFEO: Slide = {
  key: "blackfeo",
  image: "",
  imageAlt: "",
  noImage: true,
  logo: logoBlackfeo.url,
  logoAlt: "BLACKFEO",
  logoClass: "h-20 sm:h-28 md:h-32 mb-4 sm:mb-6",
  title: <>Todos os nossos produtos com<br /><span className="text-[var(--black-campaign-lilac)]">até 80% de desconto</span></>,
  desc: "Pós-graduações, certificações, STAR Cursos e Agente IA com os menores preços do ano. Escolha o seu e garanta a sua vaga.",
  descClass: "text-muted-foreground",
  tagline: "Ofertas exclusivas por tempo limitado",
  taglineClass: BLACKFEO_TAGLINE_CLS,
  ctaLabel: "Ver ofertas",
  ctaHref: "/blackfeo?ponto=slide-home",
  ctaInternal: false,
  bgClass: "blackfeo-scope bg-background text-foreground",
  bgStyle:
    "radial-gradient(40rem 28rem at 100% 0%, color-mix(in oklch, var(--primary) 35%, transparent), transparent 70%), radial-gradient(36rem 24rem at 0% 100%, color-mix(in oklch, var(--accent) 25%, transparent), transparent 70%)",
  accent: "#5b50f0",
  ctaClass: "bg-primary text-primary-foreground uppercase font-extrabold hover:brightness-110",
};

/** Slide BLACKFEO · Certificações — manda para o catálogo da ação. */
const SLIDE_BLACKFEO_CERT: Slide = {
  key: "blackfeo-cert",
  image: "",
  imageAlt: "",
  noImage: true,
  logo: logoBlackfeo.url,
  logoAlt: "BLACKFEO",
  logoClass: "h-20 sm:h-28 md:h-32 mb-4 sm:mb-6",
  eyebrow: "BLACKFEO · CERTIFICAÇÕES",
  title: <>Certificações com<br /><span className="text-[var(--black-campaign-lilac)]">75% de desconto</span></>,
  desc: "As 7 certificações da FEO com o menor preço do ano.",
  descClass: "text-muted-foreground",
  tagline: BLACKFEO_TAGLINE,
  taglineClass: BLACKFEO_TAGLINE_CLS,
  ctaLabel: "Ver certificações",
  ctaHref: "/blackfeo?ponto=slide-home-cert",
  ctaInternal: false,
  bgClass: "blackfeo-scope bg-background text-foreground",
  bgStyle:
    "radial-gradient(40rem 28rem at 100% 0%, color-mix(in oklch, var(--primary) 35%, transparent), transparent 70%), radial-gradient(36rem 24rem at 0% 100%, color-mix(in oklch, var(--accent) 25%, transparent), transparent 70%)",
  accent: "#5b50f0",
  ctaClass: "bg-primary text-primary-foreground uppercase font-extrabold hover:brightness-110",
};

/** Slide BLACKFEO · STAR Cursos — manda direto para a LP da STAR. */
const SLIDE_BLACKFEO_STAR: Slide = {
  key: "blackfeo-star",
  image: "",
  imageAlt: "",
  noImage: true,
  logo: logoBlackfeo.url,
  logoAlt: "BLACKFEO",
  logoClass: "h-20 sm:h-28 md:h-32 mb-4 sm:mb-6",
  eyebrow: "BLACKFEO · STAR CURSOS",
  title: <>STAR Cursos com<br /><span className="text-[var(--black-campaign-lilac)]">68% de desconto</span></>,
  desc: "A maior biblioteca online de fisioterapia musculoesquelética.",
  descClass: "text-muted-foreground",
  tagline: BLACKFEO_TAGLINE,
  taglineClass: BLACKFEO_TAGLINE_CLS,
  ctaLabel: "Ver oferta STAR",
  ctaHref: "/star?ponto=slide-home-star",
  ctaInternal: false,
  bgClass: "blackfeo-scope bg-background text-foreground",
  bgStyle:
    "radial-gradient(40rem 28rem at 100% 0%, color-mix(in oklch, var(--primary) 35%, transparent), transparent 70%), radial-gradient(36rem 24rem at 0% 100%, color-mix(in oklch, var(--accent) 25%, transparent), transparent 70%)",
  accent: "#5b50f0",
  ctaClass: "bg-primary text-primary-foreground uppercase font-extrabold hover:brightness-110",
};
const SLIDE_APROVAFEO: Slide = {
  key: "aprovafeo",
  image: aprovafeoBanner.url,
  imageAlt: "AprovaFEO Orto, preparatório para prova de Especialidade do COFFITO",
  eyebrow: "Traumato-Ortopedia",
  title: (
    <span className="text-balance inline-block max-w-[22ch]">
      Curso preparatório para prova de Especialidade do <span className="text-[#95a2ff]">COFFITO</span>
    </span>
  ),
  desc: "Prepare-se para a Avaliação de Especialidade com estratégia, simulados e mentoria de quem já trilhou o caminho.",
  ctaLabel: "Conheça o AprovaFEO",
  ctaHref: "/aprovafeo-orto",
  ctaInternal: true,
  bgClass: "bg-gradient-to-br from-[#13123a] via-[#1c1b58] to-[#3b42d1]",
  accent: "#95a2ff",
  ctaClass: "bg-white hover:bg-[#95a2ff] text-[#13123a]",
};

const SLIDES_BY_ACAO: Record<string, Slide> = {
  flashfeo: SLIDE_FLASHFEO,
  plantaofeo: SLIDE_PLANTAOFEO,
  semanadafisio: SLIDE_SEMANADAFISIO,
};

/** Catálogo completo dos slides, indexado pela `key`. */
export const SLIDE_REGISTRY: Record<string, Slide> = {
  black2x1: SLIDE_BLACK2X1,
  blackfeo: SLIDE_BLACKFEO,
  "blackfeo-cert": SLIDE_BLACKFEO_CERT,
  "blackfeo-star": SLIDE_BLACKFEO_STAR,
  pos: SLIDE_POS,
  cbfeo: SLIDE_CBFEO,
  cert: SLIDE_CERT,
  certquadril: SLIDE_CERT_QUADRIL,
  certquadrilvenda: {
    ...SLIDE_CERT_QUADRIL,
    key: "certquadrilvenda",
    eyebrow: "OFERTA EXCLUSIVA DE LANÇAMENTO · R$400 OFF",
    desc: "Curso 100% online, 60h · de R$697 por R$297",
    ctaLabel: "GARANTIR MINHA VAGA",
  },
  institucional: SLIDE_INSTITUCIONAL,
  aprovafeo: SLIDE_APROVAFEO,
  plantaofeo: SLIDE_PLANTAOFEO,
  flashfeo: SLIDE_FLASHFEO,
  semanadafisio: SLIDE_SEMANADAFISIO,
};

/** Metadados usados no controle do admin (/admin/banner). */
export type SlideMeta = {
  key: string;
  nome: string;
  desc: string;
  rota: string;
  /** Slug da ação em /admin/campanhas, quando o slide pertence a uma ação. */
  acaoSlug?: string;
};

export const SLIDE_META: SlideMeta[] = [
  { key: "black2x1", nome: "BLACK 2x1 2026", desc: "Pós FEO em Dobro · captação da lista", rota: "/black-lista" },
  { key: "blackfeo", nome: "BLACKFEO", desc: "Até 80% off · entra no ar quando a ação é liberada (prévia com ?preview=1)", rota: "/blackfeo" },
  { key: "blackfeo-cert", nome: "BLACKFEO · CERTIFICAÇÕES", desc: "Certificações com 75% off · catálogo da ação (mesma regra da BLACKFEO)", rota: "/blackfeo" },
  { key: "blackfeo-star", nome: "BLACKFEO · STAR", desc: "STAR com 68% off · LP da STAR (mesma regra da BLACKFEO)", rota: "/star" },
  { key: "pos", nome: "LOTE PROMO (PÓS)", desc: "As 6 pós-graduações com condição especial", rota: "/pos" },
  { key: "cert", nome: "CERTIFICAÇÕES", desc: "Catálogo das 6 certificações", rota: "/certificacao" },
  { key: "certquadril", nome: "CERT-QUADRIL GRUPO", desc: "Lançamento · grupo de pré-matrícula no WhatsApp", rota: "/certificacao-quadril" },
  { key: "certquadrilvenda", nome: "CERT-QUADRIL VENDA", desc: "Venda direta · oferta de lançamento R$400 OFF", rota: "/certificacao-quadril" },
  { key: "cbfeo", nome: "CBFEO", desc: "Congresso CBFEO ao vivo · encerrado", rota: "/cbfeo-ao-vivo" },
  { key: "institucional", nome: "INSTITUCIONAL", desc: "Feito por e para fisioterapeutas", rota: "/pos" },
  { key: "aprovafeo", nome: "APROVAFEO", desc: "Preparatório para provas e concursos", rota: "/aprovafeo-orto" },
  { key: "plantaofeo", nome: "PLANTÃO FEO", desc: "Ação de pico das pós-graduações", rota: "/plantaofeo", acaoSlug: "plantaofeo" },
  { key: "flashfeo", nome: "FLASH FEO", desc: "Ofertas relâmpago de 24 horas", rota: "/flashfeo", acaoSlug: "flashfeo" },
  { key: "semanadafisio", nome: "SEMANA DA FISIOTERAPIA", desc: "A maior ação do ano", rota: "/semanadafisio", acaoSlug: "semanadafisio" },
];

/**
 * Monta a lista de slides visíveis conforme o estado das ações.
 * Mantido apenas como fallback: o controle oficial é /admin/banner.
 */
export function selectSlides(state: AcoesState | null | undefined): Slide[] {
  const s = state ?? EMPTY_ACOES_STATE;
  const ativos = ACOES.filter((a) => s[a.slug]?.ativa);
  const hidden = new Set(ativos.map((a) => a.hidesSlideKey).filter(Boolean) as string[]);

  const base = [SLIDE_POS, SLIDE_CERT, SLIDE_INSTITUCIONAL].filter((sl) => !hidden.has(sl.key));
  const acaoSlides = ativos
    .map((a) => SLIDES_BY_ACAO[a.slug])
    .filter((sl): sl is Slide => Boolean(sl));

  return [...acaoSlides, ...base];
}

/** Monta os slides a partir da configuração manual salva em /admin/banner. */
export function selectSlidesFromConfig(config: { order: string[]; enabled: Record<string, boolean> }): Slide[] {
  return config.order
    .filter((key) => config.enabled[key])
    .map((key) => SLIDE_REGISTRY[key])
    .filter((sl): sl is Slide => Boolean(sl));
}
